// src/hooks/useAudioTranscription.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { setTranscriptHandler, startSystemCapture, stopStreaming } from "../audio/audio-client";

type SourceTag = "mic" | "system";

export type TranscriptEvents = {
  onPartial: (text: string, source: SourceTag) => void;
  onFinal: (text: string, source: SourceTag) => void;
  onStreamEnd: () => void; // unexpected end
};

export function useAudioTranscription(events: TranscriptEvents) {
  const { onPartial, onFinal, onStreamEnd } = events;

  const [transcript, setTranscript] = useState("");
  const [transcriptTail, setTranscriptTail] = useState("");
  const [shouldBeListening, setShouldBeListening] = useState(false); // user intent
  const [listening, setListening] = useState(false);                 // UI "active" flag

  // keep latest callbacks stable against re-renders
  const cbRef = useRef(events);
  useEffect(() => { cbRef.current = events; }, [events]);

  const handleStart = useCallback(() => {
    // intent → on
    setShouldBeListening(true);

    // Pre-arm handler BEFORE starting capture (prevents first-partial drop on fast machines)
    setTranscriptHandler({
      onTranscript: ({ type, text }) => {
        const source: SourceTag = "system"; // guardrail: Live uses system-only path

        if (type === "final") {
          // Original behavior: always log finals, clear tail immediately, append to transcript
          // eslint-disable-next-line no-console
          console.log(`[TRANSCRIPT final] (${source})`, text);
          setTranscriptTail("");
          setTranscript(prev => (prev ? `${prev}\n(${source}) ${text}` : `(${source}) ${text}`));
          cbRef.current.onFinal(text, source);
        } else {
          // Original behavior: always log partials, set tail directly (no throttling)
          // eslint-disable-next-line no-console
          console.log(`[TRANSCRIPT partial] (${source})`, text);
          setTranscriptTail(text);
          cbRef.current.onPartial(text, source);
        }
      },
      onStreamEnd: () => {
        setListening(false);  // keep intent true; watchdog decides recovery
        cbRef.current.onStreamEnd();
      },
    });

    startSystemCapture();
    setListening(true);
  }, []);

  const handleStop = useCallback(() => {
    setShouldBeListening(false);
    stopStreaming();
    setListening(false);
  }, []);

  return {
    transcript,
    transcriptTail,
    shouldBeListening,
    listening,
    handleStart,
    handleStop,
    // used by watchdog to keep UI "active" after soft restart
    setListening,
  };
}
